import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaExclamationTriangle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Card from '../common/Card';
import Badge from '../common/Badge';
import { useHttp } from '../../dry/hooks';

// ExceptionsBanner — the "exceptions-first" strip at the top of the manager
// dashboard. Rolls up within-scope overdue jobs, pending approvals, and
// flagged submissions into a single count. Calm when there's nothing to do,
// loud (but not alarming) when there is. Every source fetch is best-effort:
// a failed source contributes 0 rather than crashing the dashboard.

interface ExceptionsBannerProps {
  siteIdsParam: string;
}

type ExceptionCategory = 'overdue' | 'approval' | 'flagged';

interface ExceptionItem {
  id: string;
  label: string;
  detail?: string;
  category: ExceptionCategory;
  route: string;
}

const CATEGORY_LABEL: Record<ExceptionCategory, string> = {
  overdue: 'Overdue',
  approval: 'Approval',
  flagged: 'Flagged',
};

const CATEGORY_BADGE: Record<ExceptionCategory, 'danger' | 'info' | 'warning'> = {
  overdue: 'danger',
  approval: 'info',
  flagged: 'warning',
};

const ExceptionsBanner: React.FC<ExceptionsBannerProps> = ({ siteIdsParam }) => {
  const http = useHttp();
  const navigate = useNavigate();
  const [items, setItems] = useState<ExceptionItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const scopeParams: Record<string, string> = siteIdsParam ? { site_ids: siteIdsParam } : {};

    const loadOverdueJobs = async (): Promise<{ count: number; items: ExceptionItem[] }> => {
      try {
        const resp = await http.get<any>('/job-instances', {
          params: { status: 'overdue', limit: 50, ...scopeParams },
        });
        const rows: any[] = resp.data?.data || [];
        const count = typeof resp.data?.total === 'number' ? resp.data.total : rows.length;
        return {
          count,
          items: rows.map((j) => ({
            id: `job-${j.id}`,
            label: j.job_template?.name || 'Job',
            detail: j.date ? `Due ${String(j.date).slice(0, 10)}` : undefined,
            category: 'overdue' as const,
            route: `/jobs/execute/${j.id}`,
          })),
        };
      } catch {
        return { count: 0, items: [] };
      }
    };

    const loadPendingApprovals = async (): Promise<{ count: number; items: ExceptionItem[] }> => {
      try {
        const resp = await http.get<any>('/analytics/dashboard', { params: scopeParams });
        const count = resp.data?.pending_approvals || 0;
        return {
          count,
          items:
            count > 0
              ? [
                  {
                    id: 'pending-approvals',
                    label: `${count} submission${count === 1 ? '' : 's'} waiting for review`,
                    category: 'approval' as const,
                    route: '/approvals',
                  },
                ]
              : [],
        };
      } catch {
        return { count: 0, items: [] };
      }
    };

    const loadFlagged = async (): Promise<{ count: number; items: ExceptionItem[] }> => {
      try {
        const resp = await http.get<any>('/flagged-submissions', {
          params: { days: 30, limit: 50, ...scopeParams },
        });
        const rows: any[] = resp.data?.flagged || [];
        return {
          count: rows.length,
          items: rows.map((r) => ({
            id: `flagged-${r.id}`,
            label: r.asset_name || r.form_name || 'Submission',
            detail: r.site_name,
            category: 'flagged' as const,
            route: `/submissions/${r.id}`,
          })),
        };
      } catch {
        return { count: 0, items: [] };
      }
    };

    (async () => {
      const [overdue, approvals, flagged] = await Promise.all([
        loadOverdueJobs(),
        loadPendingApprovals(),
        loadFlagged(),
      ]);
      if (cancelled) return;
      setTotal(overdue.count + approvals.count + flagged.count);
      setItems([...overdue.items, ...approvals.items, ...flagged.items]);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [siteIdsParam, http]);

  // Never render nothing — the absence of a banner must never be mistaken
  // for "nothing to check". While loading, show a neutral holding state.
  if (loading) {
    return (
      <Card className="mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <FaCheckCircle />
          <span>Checking for exceptions…</span>
        </div>
      </Card>
    );
  }

  if (total === 0) {
    return (
      <Card className="mb-4">
        <div className="flex items-center gap-2 text-sm font-medium text-green-700">
          <FaCheckCircle className="text-green-500" />
          <span>All clear today</span>
        </div>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
          <FaExclamationTriangle className="text-amber-500" />
          <span>{total} need attention</span>
        </div>
        {expanded ? (
          <FaChevronUp className="text-gray-400 shrink-0" />
        ) : (
          <FaChevronDown className="text-gray-400 shrink-0" />
        )}
      </button>
      {expanded && (
        <div className="mt-3 divide-y divide-gray-100 border-t border-gray-100">
          {items.map((item) => (
            <div
              key={item.id}
              onClick={() => navigate(item.route)}
              className="py-2 px-1 flex items-center justify-between gap-3 cursor-pointer hover:bg-gray-50 rounded-sm transition"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{item.label}</div>
                {item.detail && <div className="text-xs text-gray-500">{item.detail}</div>}
              </div>
              <Badge variant={CATEGORY_BADGE[item.category]} size="sm">
                {CATEGORY_LABEL[item.category]}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default ExceptionsBanner;
